/**
 * MemberNode — the satellite that stands for a MEMBER AGENT this agent
 * consumes, or for an MCP server an operator attached to it by hand.
 *
 * It is deliberately the SAME DRAWING as ModelNode: a small round-square icon
 * with a caption underneath, joined to whatever consumes it by the same dashed
 * connector. Founder ruling (2026-08-25) on the two captioned "capability
 * bands" this replaced:
 *
 *   "don't invent new styles just use the icon of the agent … do what n8n did
 *    just like our model … a line attached to the agent that uses it, if in
 *    parent then rollup to the parent card"
 *
 * So there is ONE vocabulary on this canvas — connector + icon + caption — and
 * a consumed member is another thing hanging off a node, because that is what
 * it is. Nothing here is a chip, a band, or a badge.
 *
 * THE ICON IS THE MEMBER'S OWN. A deployed agent row carries `iconUrl`; when it
 * has none (or the file 404s, or the entry is an MCP server and not an agent at
 * all) it falls back to the letter tile every icon-less agent on this app
 * already uses. No invented glyph.
 *
 * SHAPE: a rounded SQUARE, not the model disc's circle, and that is not a
 * second style — it is this app's ONE agent-icon shape. The model disc is a
 * circle because it holds a VENDOR MARK (an inline SVG glyph on a transparent
 * ground). A marketplace agent icon is the opposite asset: a 256px macOS-style
 * badge, opaque RGB, drawn as a rounded square, sometimes on a WHITE field that
 * a circular clip leaves showing as white corners.
 *
 * Purely presentational — everything comes from `data`.
 */
import React from 'react';
import { Handle, Position } from 'reactflow';
import './MemberNode.css';

export const MemberNode = ({ data }) => {
  const {
    name = '', iconUrl = '', notReady = false, onNodeClick, tooltip, attached = true,
  } = data || {};
  const letter = (String(name).replace(/[^A-Za-z0-9]/g, '').charAt(0) || '?').toUpperCase();
  const go = () => { if (onNodeClick) onNodeClick(); };

  return (
    <div
      className={['gwe-member-node', notReady && 'is-unset'].filter(Boolean).join(' ')}
      title={tooltip || undefined}
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); go(); }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault(); e.stopPropagation(); go();
      }}
    >
      {/* Only rendered when something actually connects to it. An AGENT-level
          member has no connector at all (see memberSatellites), and a handle
          with no edge is an anchor for a line that does not exist. */}
      {attached && (
        <Handle
          type="target"
          position={Position.Left}
          id="disc"
          isConnectable={false}
          className="gwe-member-node__handle"
        />
      )}
      <div className="gwe-member-node__tile">
        {iconUrl
          ? <img src={iconUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          : <span className="gwe-member-node__letter">{letter}</span>}
      </div>
      <div className="gwe-member-node__caption">
        <div className="gwe-member-node__label">{name}</div>
      </div>
    </div>
  );
};

export default MemberNode;
